/* NgRx */
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ProjectsState } from './projects.reducer';

// Selector functions
const projectsData = createFeatureSelector<ProjectsState>('sitenotes.projects');

export const projectsList = createSelector(projectsData, state => state.projectsList);

export const projectsFilterList = createSelector(projectsData, state => state.projectsFilterList);

export const sitesFilterList = createSelector(projectsData, (state, props) => state.sitesFilterData?.[props.projectId] || []);

export const projectMembersFilterList = createSelector(projectsData, state => state.projectMembersFilterList);

export const projectAccountsInfo = createSelector(projectsData, state => state.projectAccountsInfo);

export const projectInvitationsInfo = createSelector(projectsData, state => state.projectInvitationsInfo);

export const projectSitesForInvitation = createSelector(projectsData, state => state.projectSitesForInvitation);

export const createInvitationLoading = createSelector(projectsData, state => state.createInvitationLoading);

export const projectDetails = createSelector(projectsData, state => state.projectDetails);

export const projectDocuments = createSelector(projectsData, state => state.projectDocuments);
export const projectDocumentsBreadcrumbs = createSelector(projectsData, state => state.projectDocumentsBreadcrumbs);
export const projectDocumentsCurrentRootId = createSelector(projectsData, state => state.projectDocumentsCurrentRootId);

export const siteWiseDailyReportsSummary = createSelector(projectsData, state => state.siteWiseDailyReportsSummary);

export const existingReportId = createSelector(projectsData, state => state.existingReportId);

export const projectReportsList = createSelector(projectsData, state => state.projectReportsList);

export const projectReport = createSelector(projectsData, state => state.projectReport);

export const bidSheet = createSelector(projectsData, state => state.bidSheet);

export const bidItemsListInfo = createSelector(projectsData, state => state.bidItemsListInfo);

export const settingsBidItemsListInfo = createSelector(
  projectsData,
  state => state.settingsBidItemsListInfo
);

export const bidItemFieldsListInfo = createSelector(
  projectsData,
  state => state.bidItemFieldsListInfo
);

export const settingsBidSheet = createSelector(projectsData, state => state.settingsBidSheet);

export const settingsBidItemDetailsById = createSelector(
  projectsData,
  state => state.settingsBidItemDetailsById ?? {}
);

/** Selector factory: returns bid item details for the given id. */
export const settingsBidItemDetails = (bidItemId: string) =>
  createSelector(settingsBidItemDetailsById, byId => byId[bidItemId] ?? null);

const settingsProjectMaterialsListByIdState = createSelector(
  projectsData,
  state => state.settingsProjectMaterialsListById ?? {}
);
/** Selector factory: returns project materials list for the given project id. */
export const settingsProjectMaterialsList = (projectId: string) =>
  createSelector(settingsProjectMaterialsListByIdState, byId => byId[projectId] ?? []);

export const settingsBidItemDetailsLoadingById = createSelector(
  projectsData,
  state => state.settingsBidItemDetailsLoadingById ?? {}
);

/** Selector factory: returns whether details are loading for the given bid item id. */
export const settingsBidItemDetailsLoading = (bidItemId: string) =>
  createSelector(settingsBidItemDetailsLoadingById, byId => !!byId[bidItemId]);

export const bidItemsSiteListInfo = createSelector(projectsData, state => state.bidItemsSiteListInfo);

export const changeOrderFilter = createSelector(projectsData, state => state.changeOrderFilter);

export const changeOrderFilterInSideNav = createSelector(projectsData, state => state.changeOrderFilterInSideNav);

export const changeOrderFilterInContextWithBidItems = createSelector(
  projectsData,
  state => state.changeOrderFilterInContextWithBidItems
);

export const siteDetails = createSelector(projectsData, state => state.siteDetails);

export const dailyReportsByDate = createSelector(projectsData, state => state.dailyReportsByDate);

export const dailyReport = createSelector(projectsData, state => JSON.parse(JSON.stringify(state.dailyReport)));

export const selectPayappItems = createSelector(projectsData, state => state.payappItems);

export const selectPayapps = createSelector(projectsData, state => state.payapps);

export const selectBillingSettings = createSelector(projectsData, state => state.billingSettings);

export const selectProjectCalendar = createSelector(projectsData, state => state.projectCalendar);

export const selectProjectCalendarLoading = createSelector(projectsData, state => state.projectCalendarLoading);

export const selectPayAppSummaries = createSelector(projectsData, state => state.payAppSummaries);

export const selectPayAppSummary = (payAppId: string) => createSelector(
  selectPayAppSummaries,
  (summaries) => summaries?.[payAppId] || null
);

// Pay App Templates Selectors
export const selectPayAppTemplates = createSelector(projectsData, state => state.payAppTemplates);

export const selectPayAppTemplatesLoading = createSelector(projectsData, state => state.payAppTemplatesLoading);

export const selectSelectedPayAppTemplate = createSelector(projectsData, state => state.selectedPayAppTemplate);

export const selectTemplatePreviewUrl = createSelector(projectsData, state => state.templatePreviewUrl);

export const selectDefaultPayAppTemplate = createSelector(
  selectPayAppTemplates,
  (templates) => templates?.find(t => t.is_default) || templates?.[0] || null
);

export const selectPayAppTemplateById = (templateId: string) => createSelector(
  selectPayAppTemplates,
  (templates) => templates?.find(t => t.uuid === templateId) || null
);

export const allBidItems = createSelector(projectsData, state => state.allBidItems);

export const allBidItemsFilter = createSelector(projectsData, state => state.allBidItemsFilter);

export const issueTypesLabelMap = createSelector(projectsData, state => state.issueTypesLabelMap);

export const issueTypesFilter = createSelector(projectsData, state => state.issueTypesFilter);
export const qpForMap = createSelector(projectsData, state => state.qpForMap);

export const consolidatedProjectData = createSelector(projectsData, state => state.consolidatedProjectData);

export const subContractorFilter = createSelector(projectsData, state => state.subContractorFilter);

export const laborFilter = createSelector(projectsData, state => state.laborFilter);

export const equipmentFilter = createSelector(projectsData, state => state.equipmentFilter);

export const currentLocation = createSelector(projectsData, state => state.currentLocation);

export const activityIdsListInfo = createSelector(projectsData, state => state.activityIdsListInfo);

export const dailyReportLineage = createSelector(projectsData, state => state.dailyReportLineage);

export const basisOfPayOptions = createSelector(projectsData, state => state.basisOfPayOptions);

export const changeInWorkCodeOptions = createSelector(projectsData, state => state.changeInWorkCodeOptions);

export const changeInWorkRequestListInfo = createSelector(projectsData, state => state.changeInWorkRequestListInfo);

export const changeOrdersRequestListInfo = createSelector(projectsData, state => state.changeOrdersRequestListInfo);

export const changeInWorkRequest = createSelector(projectsData, state => state.changeInWorkRequest);

export const changeOrdersRequest = createSelector(projectsData, state => state.changeOrdersRequest);

export const reportInfo = createSelector(projectsData, state => state.reportInfo);

export const lineageList = createSelector(projectsData, state => state.lineageList);

export const submittalsRegistryListInfo = createSelector(projectsData, state => state.submittalsRegistryListInfo);

export const submittalsItemsListInfo = createSelector(projectsData, state => state.submittalsItemsListInfo);

export const usersListInfo = createSelector(projectsData, state => state.usersListInfo);

export const projectRolesListInfo = createSelector(projectsData, state => state.projectRolesListInfo);

export const existingMembersListInfo = createSelector(projectsData, state => state.existingMembersListInfo);

export const submittalsItemDetails = createSelector(projectsData, state => state.submittalsItemDetails);

export const rfisListInfo = createSelector(projectsData, state => state.rfisListInfo);

export const projectMembersListInfo = createSelector(projectsData, state => state.projectMembersListInfo);

export const contractorsListInfo = createSelector(projectsData, state => state.contractorsListInfo);

export const deliveryTrackingFieldsListInfo = createSelector(
  projectsData,
  state => state.deliveryTrackingFieldsListInfo
);

export const sitesListInfo = createSelector(projectsData, state => state.sitesListInfo);

export const rfiItemDetails = createSelector(projectsData, state => state.rfiItemDetails);

export const rfiNo = createSelector(projectsData, state => state.rfiNo);

export const materialTestNameList = createSelector(projectsData, state => state.materialTestNameList);

export const labNameList = createSelector(projectsData, state => state.labNameList);

export const draftDailyReport = createSelector(projectsData, state => state.draftDailyReport);

export const laborLookupData = createSelector(projectsData, state => state.laborLookupData);

export const equipmentLookupData = createSelector(projectsData, state => state.equipmentLookupData);

export const activityTypeLookupData = createSelector(projectsData, state => state.activityTypeLookupData);

export const externalLabLookupData = createSelector(projectsData, state => state.externalLabLookupData);

export const externalLabListInfo = createSelector(projectsData, state => state.externalLabListInfo);

export const projectMaterialTestListInfo = createSelector(
  projectsData,
  state => state.projectMaterialTestListInfo
);

export const emailExistsCheck = createSelector(projectsData, state => state.emailExistsCheck);

export const phoneExistsCheck = createSelector(projectsData, state => state.phoneExistsCheck);

export const availableCors = createSelector(projectsData, (state: any) => state?.availableCors);

export const batchTicketData = createSelector(projectsData, state => state.batchTicketData);
export const batchTicketLoading = createSelector(projectsData, state => state.batchTicketLoading);

export const warrantiesInfo = createSelector(projectsData, state => state.warrantiesInfo);
export const warrantyClaimsInfo = createSelector(projectsData, state => state.warrantyClaimsInfo);
export const warrantyClaimDetail = createSelector(projectsData, state => state.warrantyClaimDetail);
